// components/SelectListDialogTable.jsx
'use client';
import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Radio,
  Checkbox,
  TablePagination,
  Box,
  Tooltip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Select,
  MenuItem,
  Paper,
  Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { useLanguage } from '../utils/i18n';

export default function SelectListDialogTable({
  multiSelect = false,
  page,
  pageSize,
  pageSizeOptions = [10, 25, 50, 100],
  onPageChange,
  onPageSizeChange,
  totalRecord = 0,
  listOptions = null,
  open,
  onClose,
  title = 'Select',
  options = [],
  columns = [],
  getOptionId = (o) => (o == null ? o : (o.id ?? o.value ?? o)),
  getOptionLabel = (o) => (o == null ? '' : (o.label ?? o.name ?? String(o))),
  value = null,
  onSelect,
  infoMessage = null,
  allowAdd = false,
  AddComponent = null,
  onAddSuccess = null,
  searchKey = 'name',
  searchPlaceholder,
  dialogProps = {},
  onSearch,
  isLoading = false,
  onLoadMore, // NEW PROP
  isRowDisabled = null, // NEW PROP

  filterOptions = [],
  filterField = '',
  defaultFilterValue = 'all'
}) {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(multiSelect ? (Array.isArray(value) ? value : value ? [value] : []) : (value ?? null));
  const [openAdd, setOpenAdd] = useState(false);
  const { t } = useLanguage();

  const formatSearchKey = (key) => {
    if (!key) return '';
    return key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase())
      .trim();
  };

  const effectivePlaceholder =
    searchPlaceholder ||
    (searchKey === 'all'
      ? 'Search all columns...'
      : searchKey && searchKey !== 'name'
        ? `Search by ${formatSearchKey(searchKey)}...`
        : t('general.searchByName', 'Search by Name...'));

  const [filterValue, setFilterValue] = useState(defaultFilterValue);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');

  useEffect(() => {
    setSelectedId(multiSelect ? (Array.isArray(value) ? value : value ? [value] : []) : (value ?? null));
  }, [multiSelect, value]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleScroll = (e) => {
    if (!onLoadMore) return;
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 20) {
      onLoadMore();
    }
  };

  const normalized = useMemo(() => {
    const dataToUse = listOptions != null ? listOptions : options;
    return (dataToUse || []).map((o) => ({
      id: getOptionId(o),
      label: getOptionLabel(o),
      raw: o
    }));
  }, [options, listOptions, getOptionId, getOptionLabel]);

  const filtered = useMemo(() => {
    let result = [...normalized];
    if (filterOptions.length > 0 && filterField && filterValue !== 'all') {
      result = result.filter((o) => o.raw?.[filterField] === filterValue);
    }

    if (!onSearch) {
      const q = (search || '').trim().toLowerCase();
      if (q) {
        result = result.filter((o) => {
          if ((o.label || '').toLowerCase().includes(q)) return true;
          return columns.some((col) => {
            const val = o.raw?.[col.field];
            return val != null && String(val).toLowerCase().includes(q);
          });
        });
      }
    }

    if (orderBy) {
      result.sort((a, b) => {
        let valA = a.raw?.[orderBy] ?? '';
        let valB = b.raw?.[orderBy] ?? '';
        if (typeof valA === 'string' && valA.includes(' km')) valA = parseFloat(valA);
        if (typeof valB === 'string' && valB.includes(' km')) valB = parseFloat(valB);

        if (valA < valB) return order === 'asc' ? -1 : 1;
        if (valA > valB) return order === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [normalized, search, onSearch, columns, filterOptions, filterField, filterValue, order, orderBy]);

  const handleSearchChange = (e) => {
    const text = e.target.value;
    setSearch(text);
    if (onSearch) {
      onSearch(text);
    }
  };

  const handleConfirm = () => {
    onSelect && onSelect(selectedId);
    onClose && onClose();
  };

  const handleClear = () => {
    if (multiSelect) setSelectedId([]);
    onSelect && onSelect(null);
    onClose && onClose();
  };

  const [isSelectingAll, setIsSelectingAll] = useState(false);

  useEffect(() => {
    if (isSelectingAll && !isLoading) {
      if (filtered.length > 0) {
        setSelectedId(filtered.map((opt) => opt.id));
        setIsSelectingAll(false);
      }
    }
  }, [isLoading, isSelectingAll, filtered]);

  const handleSelectAll = () => {
    const selArr = Array.isArray(selectedId) ? selectedId : [];
    if (selArr.length === filtered.length && filtered.length > 0) {
      setSelectedId([]);
    } else {
      if (totalRecord && filtered.length < totalRecord && onPageSizeChange) {
        onPageSizeChange(totalRecord > 0 ? totalRecord : 10000);
        setIsSelectingAll(true);
      } else {
        setSelectedId(filtered.map((opt) => opt.id));
      }
    }
  };

  const outerFullWidth = dialogProps.fullWidth ?? true;
  const outerMaxWidth = dialogProps.maxWidth ?? 'md';
  const outerFullScreen = dialogProps.fullScreen ?? false;

  return (
    <>
      <Dialog open={!!open} onClose={onClose} fullWidth={outerFullWidth} maxWidth={outerMaxWidth} fullScreen={outerFullScreen}>
        <DialogTitle>{title}</DialogTitle>

        <DialogContent dividers sx={{ p: 2 }}>
          {infoMessage && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 1 }}>
              {infoMessage}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              placeholder={effectivePlaceholder}
              value={search}
              onChange={handleSearchChange}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: isLoading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={20} color="inherit" />
                  </InputAdornment>
                ) : null
              }}
            />

            {/* DROPDOWN FILTER KUSTOM */}
            {filterOptions.length > 0 && (
              <Select
                size="small"
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                sx={{ minWidth: 150, bgcolor: '#fff' }}
              >
                {filterOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            )}

            {allowAdd && AddComponent && (
              <Tooltip title="Add new">
                <IconButton color="primary" onClick={() => setOpenAdd(true)} sx={{ bgcolor: '#eff6ff', borderRadius: 1 }}>
                  <AddIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {multiSelect && filtered.length > 0 && (
            <Box sx={{ pb: 1, display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={Array.isArray(selectedId) && selectedId.length > 0 && selectedId.length === filtered.length}
                indeterminate={Array.isArray(selectedId) && selectedId.length > 0 && selectedId.length < filtered.length}
                onChange={handleSelectAll}
                size="small"
              />
              <span style={{ fontSize: '0.875rem', cursor: 'pointer' }} onClick={handleSelectAll}>
                {t('dataTable.selectAll', 'Select All')}
              </span>
            </Box>
          )}

          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }} onScroll={handleScroll}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell
                      key={col.field}
                      sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', width: col.width || 'auto' }}
                      align={col.align || 'left'}
                      sortDirection={orderBy === col.field ? order : false} // Indikator sortir di cell
                    >
                      {/* KOMPONEN LABEL SORTIR */}
                      <TableSortLabel
                        active={orderBy === col.field}
                        direction={orderBy === col.field ? order : 'asc'}
                        onClick={() => handleRequestSort(col.field)}
                      >
                        {col.headerName}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', width: '80px' }}>
                    {t('general.action', 'Action')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {isLoading ? t('general.searching', 'Mencari data...') : t('general.noData', 'Tidak ada data ditemukan')}
                    </TableCell>
                  </TableRow>
                )}
                {filtered.map((opt) => {
                  const isSelected = multiSelect
                    ? Array.isArray(selectedId) && selectedId.some((v) => String(v) === String(opt.id))
                    : String(selectedId) === String(opt.id);

                  const handleRowClick = () => {
                    if (isRowDisabled && isRowDisabled(opt.raw)) return;
                    if (multiSelect) {
                      setSelectedId((prev) => {
                        const arr = Array.isArray(prev) ? prev : [];
                        if (arr.some((v) => String(v) === String(opt.id))) {
                          return arr.filter((v) => String(v) !== String(opt.id));
                        }
                        return [...arr, opt.id];
                      });
                    } else {
                      setSelectedId(opt.id);
                    }
                  };

                  const disabled = isRowDisabled ? isRowDisabled(opt.raw) : false;

                  return (
                    <TableRow
                      key={String(opt.id)}
                      hover={!disabled}
                      onClick={handleRowClick}
                      selected={isSelected}
                      sx={{
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        opacity: disabled ? 0.6 : 1,
                        '&.Mui-selected, &.Mui-selected:hover': { bgcolor: '#eff6ff' }
                      }}
                    >
                      {columns.map((col) => (
                        <TableCell key={col.field} align={col.align || 'left'}>
                          {col.renderCell ? col.renderCell(opt.raw) : (opt.raw[col.field] ?? '-')}
                        </TableCell>
                      ))}
                      <TableCell align="center" padding="checkbox">
                        {multiSelect ? (
                          <Checkbox checked={isSelected} disabled={disabled} size="small" />
                        ) : (
                          <Radio checked={isSelected} disabled={disabled} size="small" />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {onPageChange && (
            <TablePagination
              component="div"
              count={totalRecord}
              page={page}
              onPageChange={(e, newPage) => onPageChange(newPage)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
              rowsPerPageOptions={pageSizeOptions}
            />
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClear} color="error">
            {t('buttonTitle.clear', 'Clear')}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={onClose} color="inherit">
            {t('buttonTitle.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={multiSelect ? !Array.isArray(selectedId) || selectedId.length === 0 : selectedId == null}
            disableElevation
          >
            {t('general.select', t('select', 'Select'))}
          </Button>
        </DialogActions>
      </Dialog>

      {allowAdd && AddComponent && (
        <AddComponent
          open={openAdd}
          id={null}
          onClose={() => setOpenAdd(false)}
          onSuccess={(createdOrMsg) => {
            if (onAddSuccess) onAddSuccess(createdOrMsg);
            setOpenAdd(false);
          }}
        />
      )}
    </>
  );
}

SelectListDialogTable.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string,
  options: PropTypes.array,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      headerName: PropTypes.string,
      width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      align: PropTypes.oneOf(['left', 'center', 'right', 'justify', 'inherit']),
      renderCell: PropTypes.func
    })
  ),
  getOptionId: PropTypes.func,
  getOptionLabel: PropTypes.func,
  value: PropTypes.any,
  onSelect: PropTypes.func,
  infoMessage: PropTypes.node,
  allowAdd: PropTypes.bool,
  AddComponent: PropTypes.elementType,
  onAddSuccess: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  dialogProps: PropTypes.object,
  onSearch: PropTypes.func,
  isLoading: PropTypes.bool,
  filterOptions: PropTypes.array,
  filterField: PropTypes.string,
  defaultFilterValue: PropTypes.string
};
